import {
  Button,
  IconArrowLeft,
  IconArrowRight,
  InformationalScreen,
  ListCard,
  ListItem,
  LoadingModal,
  Modal,
  Text,
  useDirection,
} from '@adc/parallax-component-library';
import { useQuery } from '@tanstack/react-query';
import { MainContainer } from 'Components/utility';
import { useCallback } from 'react';
import { getCareTeamConnections } from 'Services/clm';
import mediator from 'Utilities/mediator';
import { type RootState } from 'src/reducers';
import { connect, type ConnectedProps } from 'react-redux';
import { notifyApp } from 'Utilities/appCommunicator';

const mapStateToProps = ({
  auth: { account_id: accountId },
  nav: {
    query: { osType, osVersion, appVersion },
  },
}: RootState) => ({
  accountId,
  osType,
  osVersion,
  appVersion,
});

const connector = connect(mapStateToProps);

type Props = ConnectedProps<typeof connector>;

const TEST_ID = 'CareTeamConnectionsList' as const;

const CareTeamConnectionsList = ({ accountId, appVersion, osType, osVersion }: Props) => {
  const isLtr = useDirection() === 'ltr';

  const { data, isLoading } = useQuery({
    queryKey: ['careTeamConnections', accountId],
    initialData: [],
    refetchOnWindowFocus: true,
    queryFn: () => getCareTeamConnections({ userId: accountId, appVersion, osType, osVersion }),
  });

  const handleClickBack = useCallback(() => {
    mediator.publish('router:back');
    notifyApp('adc-webview:navigate-back-app');
  }, []);

  const handleDone = useCallback(() => {
    notifyApp('adc-webview:connections', { data });
  }, [data]);

  const navigateToAddConnection = useCallback(() => {
    mediator.publish('router:navigate', '/add-connection');
    Modal.hide();
  }, []);

  const handleAddNewCareTeam = useCallback(() => {
    if (data.length >= 1) {
      if (data.findIndex((d) => d.status === 'connected') !== -1) {
        Modal.show({
          testID: `${TEST_ID}.replace-current`,
          header: 'Replace Current Care Team?',
          details:
            'The connection to your current care team will be removed when your new care team accepts your request. Are you sure you want to proceed?',
          buttonLayout: 'row',
          primaryAction: {
            label: 'Proceed',
            onPress: navigateToAddConnection,
          },
          secondaryAction: {
            label: 'Cancel',
            onPress: () => {
              Modal.hide();
            },
          },
        });
      } else {
        Modal.show({
          testID: `${TEST_ID}.replace-pending`,
          header: 'Replace Pending Invite?',
          details: 'Sending a new invite will replace the pending one. Do you want to continue?',
          buttonLayout: 'row',
          primaryAction: {
            label: 'Proceed',
            onPress: navigateToAddConnection,
          },
          secondaryAction: {
            label: 'Cancel',
            onPress: () => {
              Modal.hide();
            },
          },
        });
      }
    } else {
      mediator.publish('router:navigate', '/add-connection');
    }
  }, [data, navigateToAddConnection]);

  return (
    <MainContainer>
      {isLoading && <LoadingModal aria-label="Loading" />}
      <InformationalScreen>
        <InformationalScreen.Page
          topNavigationProps={{
            title: 'Connections',
            buttonStart: {
              element: isLtr ? (
                <IconArrowLeft testID={`${TEST_ID}.back-button`} />
              ) : (
                <IconArrowRight testID={`${TEST_ID}.back-button`} />
              ),
              label: 'Back',
              onPress: handleClickBack,
            },
            testID: `${TEST_ID}.title`,
          }}
        >
          <InformationalScreen.Content>
            <Text marginTop={-8} marginBottom="$2">
              {data.length > 0
                ? "Here's a list of care teams that have access to your information."
                : "You're not connected with any care teams yet."}
            </Text>

            <ListCard>
              {data.map(({ id, name, status }) => (
                <ListItem
                  // roundedBottom
                  // roundedTop
                  key={id}
                  title={`${name} - ${id}`}
                  label={
                    status === 'connected'
                      ? 'Current Care Team'
                      : 'Invite Pending With New Care Team'
                  }
                  testID={`${TEST_ID}.item-${id}`}
                />
              ))}
            </ListCard>

            <Button
              variant="secondary"
              label="Add a New Care Team"
              testID={`${TEST_ID}.add-button`}
              onPress={handleAddNewCareTeam}
            />
          </InformationalScreen.Content>
          <InformationalScreen.Footer>
            <Button label="Done" testID={`${TEST_ID}.done-button`} onPress={handleDone} />
          </InformationalScreen.Footer>
        </InformationalScreen.Page>
      </InformationalScreen>
    </MainContainer>
  );
};

export default connector(CareTeamConnectionsList);
